import { z } from 'zod';
import { projectTemplateApplySchema } from './project-templates';
import { paginatedSchema, paginationQuerySchema } from './pagination';
import { TRAFFIC_LIGHTS } from './status';

export const PROJECT_PHASES = ['DIAGNOSIS', 'IMPLEMENTATION', 'MONITORING'] as const;
export type ProjectPhaseName = (typeof PROJECT_PHASES)[number];

/** Fases do projeto BSC (PRD §8). */
export const PROJECT_PHASE_LABELS: Record<ProjectPhaseName, string> = {
  DIAGNOSIS: 'Diagnóstico',
  IMPLEMENTATION: 'Implantação',
  MONITORING: 'Acompanhamento',
};

export const ORGANIZATION_STATUSES = ['ACTIVE', 'INACTIVE'] as const;
export const ORGANIZATION_STATUS_LABELS: Record<(typeof ORGANIZATION_STATUSES)[number], string> = {
  ACTIVE: 'Ativa',
  INACTIVE: 'Inativa',
};

export const projectSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  phase: z.enum(PROJECT_PHASES),
  horizonStart: z.number().int(),
  horizonEnd: z.number().int(),
});
export type ProjectSummary = z.infer<typeof projectSummarySchema>;

/** Filtros da Central de Empresas. */
export const organizationListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(100, 'Busca longa demais.').optional(),
});
export type OrganizationListQuery = z.input<typeof organizationListQuerySchema>;

/** Empresa cliente na Central de Empresas (visão da Fluxus). */
export const centralOrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  legalName: z.string().nullable(),
  status: z.enum(ORGANIZATION_STATUSES),
  activeProject: projectSummarySchema.nullable(),
  consultant: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  /** Projetos ativos da empresa (a linha mostra o mais recente). */
  activeProjects: z.number().int(),
  /** Performance da empresa no mês de referência do projeto (o último até hoje com dado — DEC-059). */
  performance: z
    .object({
      value: z.number(),
      light: z.enum(TRAFFIC_LIGHTS),
      year: z.number().int(),
      month: z.number().int(),
    })
    .nullable(),
  /** Status do projeto (DEC-059): semáforo da performance; azul em Diagnóstico; cinza sem projeto ativo ou sem dado. */
  projectStatus: z.enum(TRAFFIC_LIGHTS),
});
export type CentralOrganization = z.infer<typeof centralOrganizationSchema>;
export const centralOrganizationListSchema = paginatedSchema(centralOrganizationSchema);

/** Indicadores de topo da Central de Empresas (PRD §11.2; DEC-059), só das empresas acessíveis e ativas. */
export const centralSummarySchema = z.object({
  activeCompanies: z.number().int(),
  projectsInImplementation: z.number().int(),
  projectsInMonitoring: z.number().int(),
  monitoredObjectives: z.number().int(),
  /** KPIs vermelhos no mês de referência de cada projeto. */
  criticalKpis: z.number().int(),
  lateActions: z.number().int(),
});
export type CentralSummary = z.infer<typeof centralSummarySchema>;

/** A empresa do usuário logado (Visão Geral). */
export const currentOrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  legalName: z.string().nullable(),
  activeProject: projectSummarySchema.nullable(),
});
export type CurrentOrganization = z.infer<typeof currentOrganizationSchema>;

/** CNPJ só com dígitos; confere os dois dígitos verificadores. */
export function isValidCnpj(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) return false;
  const calc = (base: string) => {
    const weights =
      base.length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = [...base].reduce((acc, d, i) => acc + Number(d) * weights[i]!, 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  const first = calc(digits.slice(0, 12));
  const second = calc(digits.slice(0, 12) + first);
  return digits.endsWith(`${first}${second}`);
}

/** "12.345.678/0001-95" a partir dos dígitos. */
export function formatCnpj(digits: string): string {
  const d = digits.replace(/\D/g, '');
  if (d.length !== 14) return digits;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** CNPJ opcional: aceita com ou sem pontuação e guarda só os dígitos. */
export const cnpjSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\D/g, ''))
  .refine((v) => v === '' || isValidCnpj(v), 'CNPJ inválido.')
  .transform((v) => (v === '' ? null : v));

/** Dados de cadastro de uma empresa cliente (criar e editar). */
export const organizationInputSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome da empresa.').max(120, 'Nome longo demais.'),
  legalName: z
    .string()
    .trim()
    .max(200, 'Razão social longa demais.')
    .transform((v) => (v === '' ? null : v))
    .nullish(),
  document: cnpjSchema.nullish(),
});
export type OrganizationInput = z.input<typeof organizationInputSchema>;

export const organizationStatusInputSchema = z.object({ status: z.enum(ORGANIZATION_STATUSES) });

export const PROJECT_STATUSES = ['ACTIVE', 'ARCHIVED'] as const;
export const MIN_HORIZON_YEAR = 2000;
export const MAX_HORIZON_YEAR = 2100;
/** Horizonte estratégico máximo (anos) de um projeto BSC. */
export const MAX_HORIZON_YEARS = 20;

/** Projeto BSC de uma empresa (detalhe da empresa). */
export const projectSchema = projectSummarySchema.extend({
  status: z.enum(PROJECT_STATUSES),
  consultant: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
});
export type Project = z.infer<typeof projectSchema>;

const horizonYear = z.coerce
  .number()
  .int('Ano inválido.')
  .min(MIN_HORIZON_YEAR, `Ano a partir de ${MIN_HORIZON_YEAR}.`)
  .max(MAX_HORIZON_YEAR, `Ano até ${MAX_HORIZON_YEAR}.`);

/** Fim depois do início e no máximo MAX_HORIZON_YEARS anos (projeto e planejamento estratégico). */
function checkHorizon(v: { horizonStart: number; horizonEnd: number }, ctx: z.RefinementCtx) {
  if (v.horizonEnd < v.horizonStart) {
    ctx.addIssue({
      code: 'custom',
      path: ['horizonEnd'],
      message: 'O fim do horizonte vem depois do início.',
    });
  } else if (v.horizonEnd - v.horizonStart > MAX_HORIZON_YEARS) {
    ctx.addIssue({
      code: 'custom',
      path: ['horizonEnd'],
      message: `O horizonte pode ter no máximo ${MAX_HORIZON_YEARS} anos.`,
    });
  }
}

/** Horizonte estratégico do projeto (anos de início e fim). */
export const horizonInputSchema = z
  .object({ horizonStart: horizonYear, horizonEnd: horizonYear })
  .superRefine(checkHorizon);
export type HorizonInput = z.input<typeof horizonInputSchema>;

const projectFields = z.object({
  name: z.string().trim().min(2, 'Informe o nome do projeto.').max(120, 'Nome longo demais.'),
  horizonStart: horizonYear,
  horizonEnd: horizonYear,
  phase: z.enum(PROJECT_PHASES),
  consultantId: z.string().uuid('Consultor inválido.').nullish(),
});

export const projectInputSchema = projectFields.superRefine(checkHorizon);
export type ProjectInput = z.input<typeof projectInputSchema>;

/** Abrir projeto: os dados do projeto e, opcionalmente, o modelo de onde ele parte (T-204; DEC-070). */
export const projectCreateInputSchema = projectFields
  .extend({ template: projectTemplateApplySchema.nullish() })
  .superRefine(checkHorizon);
export type ProjectCreateInput = z.input<typeof projectCreateInputSchema>;

/** Empresa cliente com cadastro, logotipo e projetos (detalhe na Central de Empresas). */
export const organizationDetailSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  legalName: z.string().nullable(),
  document: z.string().nullable(),
  status: z.enum(ORGANIZATION_STATUSES),
  /** URL assinada e temporária do logotipo (null sem logotipo). */
  logoUrl: z.string().nullable(),
  createdAt: z.string(),
  projects: z.array(projectSchema),
});
export type OrganizationDetail = z.infer<typeof organizationDetailSchema>;

/** Consultor Fluxus (para escolher o responsável por um projeto). */
export const consultantOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
});
export const consultantOptionsSchema = z.object({ items: z.array(consultantOptionSchema) });

/** Tamanho máximo do logotipo (bytes). */
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
